/**
 * Table parser — converts <w:tbl> elements to ProseMirror table nodes.
 *
 * OOXML table structure:
 *   <w:tbl>
 *     <w:tblPr> (table properties)
 *     <w:tblGrid> (column widths)
 *       <w:gridCol w:w="..." /> ...
 *     <w:tr> (table row)
 *       <w:trPr> (row properties, e.g. header row)
 *       <w:tc> (table cell)
 *         <w:tcPr> (cell properties: gridSpan, vMerge, shading, borders)
 *         <w:p> ... (cell content — one or more paragraphs)
 *
 * ProseMirror table structure:
 *   table
 *     tableRow
 *       tableCell/tableHeader { colspan, rowspan, colwidth, background, borders, verticalAlign, padding }
 *         paragraph ...
 */

import { PMNode, ParseContext, StyleDefinition, TableStyleConditional, TableLook, RunProperties } from './types';
import { getChildNS, W_NS, twipsToPoints } from './zipReader';
import { parseParagraph } from './paragraphParser';
import { CommentTracker } from './commentParser';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Find the <w:sdtContent> child inside a <w:sdt> element. */
function findSdtContent(sdtEl: Element): Element | null {
  const children = sdtEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');
    if (localName === 'sdtContent') return el;
  }
  return null;
}

/** Read an OOXML attribute, trying namespace first then prefixed fallback. */
function getAttr(el: Element, name: string): string {
  return el.getAttributeNS(W_NS, name) || el.getAttribute(`w:${name}`) || '';
}

/** Convert twips to px (at 96 dpi). */
function twipsToPx(twips: number): number {
  return Math.round(twipsToPoints(twips) * 1.333);
}

// ---------------------------------------------------------------------------
// Border parsing
// ---------------------------------------------------------------------------

/** Parsed border info in a CSS-compatible format: "1pt solid #000000" */
interface ParsedBorders {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  insideH?: string;
  insideV?: string;
}

/** Map OOXML border style values to CSS border styles. */
function ooxmlBorderStyleToCSS(val: string): string | null {
  const map: Record<string, string> = {
    single: 'solid',
    thick: 'solid',
    double: 'double',
    dashed: 'dashed',
    dotted: 'dotted',
    dashSmallGap: 'dashed',
    dashDotStroked: 'dashed',
    threeDEmboss: 'ridge',
    threeDEngrave: 'groove',
    outset: 'outset',
    inset: 'inset',
    nil: 'none',
    none: 'none',
  };
  return map[val] || 'solid';
}

/**
 * Parse a single border element (e.g. <w:top w:val="single" w:sz="4" w:color="000000"/>)
 * into a CSS border string like "0.5pt solid #000000".
 */
function parseSingleBorder(borderEl: Element | null): string | undefined {
  if (!borderEl) return undefined;
  const val = getAttr(borderEl, 'val');
  if (!val || val === 'nil' || val === 'none') return undefined;

  const sz = getAttr(borderEl, 'sz');
  const color = getAttr(borderEl, 'color');
  const style = ooxmlBorderStyleToCSS(val) || 'solid';

  // sz is in eighths of a point; default to 4 (0.5pt) if missing
  const sizeRaw = sz ? parseInt(sz, 10) : 4;
  // sz="0" means no visible border — treat as undefined so table defaults can apply
  if (sizeRaw === 0) return undefined;
  const sizePt = sizeRaw / 8;
  const hexColor = color && color !== 'auto' ? `#${color}` : '#000000';

  return `${sizePt}pt ${style} ${hexColor}`;
}

/**
 * Parse a borders container element (e.g. <w:tblBorders> or <w:tcBorders>)
 * which contains child elements like <w:top>, <w:bottom>, <w:left>, <w:right>,
 * <w:insideH>, <w:insideV>.
 */
function parseBordersElement(bordersEl: Element | null): ParsedBorders {
  const result: ParsedBorders = {};
  if (!bordersEl) return result;

  result.top = parseSingleBorder(getChildNS(bordersEl, W_NS, 'top'));
  result.bottom = parseSingleBorder(getChildNS(bordersEl, W_NS, 'bottom'));
  result.left = parseSingleBorder(getChildNS(bordersEl, W_NS, 'left'));
  result.right = parseSingleBorder(getChildNS(bordersEl, W_NS, 'right'));
  // "start"/"end" are used in newer OOXML as aliases for left/right
  if (!result.left) result.left = parseSingleBorder(getChildNS(bordersEl, W_NS, 'start'));
  if (!result.right) result.right = parseSingleBorder(getChildNS(bordersEl, W_NS, 'end'));
  result.insideH = parseSingleBorder(getChildNS(bordersEl, W_NS, 'insideH'));
  result.insideV = parseSingleBorder(getChildNS(bordersEl, W_NS, 'insideV'));

  return result;
}

// ---------------------------------------------------------------------------
// Table style resolution
// ---------------------------------------------------------------------------

/**
 * Resolved table style info to pass down to cells.
 * Contains the table look flags and conditional formatting entries.
 */
interface ResolvedTableStyle {
  look: TableLook;
  /** Whole-table default cell shading */
  wholeTableShading?: string;
  /** Whole-table default run properties */
  wholeTableRunProps?: RunProperties;
  /** Conditional formatting entries (e.g., band1Horz, firstRow, etc.) */
  conditionals: Map<string, TableStyleConditional>;
  /** Style-level table borders */
  styleBorders: ParsedBorders;
}

/**
 * Resolve a table's style by reading <w:tblStyle> and <w:tblLook> from <w:tblPr>,
 * then looking up the style definition (with basedOn chain) from ctx.styles.
 */
function resolveTableStyle(tblEl: Element, ctx: ParseContext): ResolvedTableStyle {
  const result: ResolvedTableStyle = {
    look: {},
    conditionals: new Map(),
    styleBorders: {},
  };

  const tblPr = getChildNS(tblEl, W_NS, 'tblPr');
  if (!tblPr) return result;

  // Read <w:tblLook> flags
  const tblLookEl = getChildNS(tblPr, W_NS, 'tblLook');
  if (tblLookEl) {
    const getFlag = (name: string) => {
      const v = tblLookEl.getAttributeNS(W_NS, name) || tblLookEl.getAttribute(`w:${name}`) || '';
      return v === '1' || v === 'true';
    };
    result.look = {
      firstRow: getFlag('firstRow'),
      lastRow: getFlag('lastRow'),
      firstColumn: getFlag('firstColumn'),
      lastColumn: getFlag('lastColumn'),
      bandRow: !getFlag('noHBand'), // noHBand=0 means banded rows are ON
      bandColumn: !getFlag('noVBand'),
    };
  }

  // Read <w:tblStyle> reference
  const tblStyleEl = getChildNS(tblPr, W_NS, 'tblStyle');
  if (!tblStyleEl) return result;
  const styleId = tblStyleEl.getAttributeNS(W_NS, 'val') || tblStyleEl.getAttribute('w:val') || '';
  if (!styleId) return result;

  // Walk the basedOn chain to collect all table style properties
  const visited = new Set<string>();
  let currentId: string | undefined = styleId;
  // Collect styles from most specific to least specific
  const chain: StyleDefinition[] = [];
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const styleDef = ctx.styles.get(currentId);
    if (!styleDef || styleDef.type !== 'table') break;
    chain.push(styleDef);
    currentId = styleDef.basedOn;
  }

  // Merge from least specific (base) to most specific (derived)
  for (let i = chain.length - 1; i >= 0; i--) {
    const sd = chain[i];
    if (sd.tableCellShading) result.wholeTableShading = sd.tableCellShading;
    if (sd.runProperties) result.wholeTableRunProps = { ...result.wholeTableRunProps, ...sd.runProperties };
    if (sd.tableBorders) {
      const tb = sd.tableBorders;
      if (tb.top) result.styleBorders.top = tb.top;
      if (tb.bottom) result.styleBorders.bottom = tb.bottom;
      if (tb.left) result.styleBorders.left = tb.left;
      if (tb.right) result.styleBorders.right = tb.right;
      if (tb.insideH) result.styleBorders.insideH = tb.insideH;
      if (tb.insideV) result.styleBorders.insideV = tb.insideV;
    }
    if (sd.tableStyleProperties) {
      sd.tableStyleProperties.forEach((cond, condType) => {
        // More specific style wins for the same condition type
        const existing = result.conditionals.get(condType);
        if (existing) {
          // Merge: specific overrides base
          result.conditionals.set(condType, {
            cellShading: cond.cellShading ?? existing.cellShading,
            runProperties: cond.runProperties
              ? { ...existing.runProperties, ...cond.runProperties }
              : existing.runProperties,
            borders: cond.borders ?? existing.borders,
          });
        } else {
          result.conditionals.set(condType, { ...cond });
        }
      });
    }
  }

  return result;
}

/**
 * Determine which conditional formatting entries apply to a cell
 * based on its position and the table look flags.
 * Returns them in priority order (lowest priority first).
 */
function getApplicableConditionals(
  style: ResolvedTableStyle,
  rowIdx: number,
  colIdx: number,
  totalRows: number,
  totalCols: number
): TableStyleConditional[] {
  const result: TableStyleConditional[] = [];
  const { look, conditionals } = style;

  // Band rows (lowest priority among conditionals)
  if (look.bandRow) {
    // Adjust row index for banding — skip header row if it's active
    const bandRowIdx = look.firstRow ? rowIdx - 1 : rowIdx;
    if (bandRowIdx >= 0) {
      const bandKey = bandRowIdx % 2 === 0 ? 'band1Horz' : 'band2Horz';
      const band = conditionals.get(bandKey);
      if (band) result.push(band);
    }
  }

  // Band columns
  if (look.bandColumn) {
    const bandKey = colIdx % 2 === 0 ? 'band1Vert' : 'band2Vert';
    const band = conditionals.get(bandKey);
    if (band) result.push(band);
  }

  // First/last column
  if (look.firstColumn && colIdx === 0) {
    const fc = conditionals.get('firstCol');
    if (fc) result.push(fc);
  }
  if (look.lastColumn && colIdx === totalCols - 1) {
    const lc = conditionals.get('lastCol');
    if (lc) result.push(lc);
  }

  // First/last row (highest priority)
  if (look.firstRow && rowIdx === 0) {
    const fr = conditionals.get('firstRow');
    if (fr) result.push(fr);
  }
  if (look.lastRow && rowIdx === totalRows - 1) {
    const lr = conditionals.get('lastRow');
    if (lr) result.push(lr);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Table-level defaults
// ---------------------------------------------------------------------------

interface TableDefaults {
  /** Default borders from <w:tblBorders> including insideH/insideV */
  borders: ParsedBorders;
  /** Default cell margins from <w:tblCellMar> (in px) */
  cellMargins: { top?: number; bottom?: number; left?: number; right?: number };
}

function parseTableDefaults(tblEl: Element): TableDefaults {
  const defaults: TableDefaults = { borders: {}, cellMargins: {} };
  const tblPr = getChildNS(tblEl, W_NS, 'tblPr');
  if (!tblPr) return defaults;

  // Table-level borders
  defaults.borders = parseBordersElement(getChildNS(tblPr, W_NS, 'tblBorders'));

  // Table-level default cell margins
  const tblCellMar = getChildNS(tblPr, W_NS, 'tblCellMar');
  if (tblCellMar) {
    const parseMar = (name: string): number | undefined => {
      const el = getChildNS(tblCellMar!, W_NS, name);
      if (!el) return undefined;
      const w = getAttr(el, 'w');
      if (!w) return undefined;
      const type = getAttr(el, 'type');
      // If type is "dxa" (twips) or absent, convert. Otherwise treat as zero.
      if (type && type !== 'dxa') return undefined;
      // Cap cell margins at 5px so narrow columns aren't starved of content space.
      // Word's default (108 twips ≈ 9px) is generous for a full-width page but
      // too much for our narrower editor.
      return Math.min(twipsToPx(parseInt(w, 10)), 5);
    };
    defaults.cellMargins.top = parseMar('top');
    defaults.cellMargins.bottom = parseMar('bottom');
    defaults.cellMargins.left = parseMar('left') ?? parseMar('start');
    defaults.cellMargins.right = parseMar('right') ?? parseMar('end');
  }

  return defaults;
}

/**
 * Parse table width from <w:tblPr><w:tblW>.
 * Returns a CSS-compatible width string (e.g. "500px" or "100%"), or null.
 */
function parseTableWidth(tblEl: Element): string | null {
  const tblPr = getChildNS(tblEl, W_NS, 'tblPr');
  if (!tblPr) return null;
  const tblW = getChildNS(tblPr, W_NS, 'tblW');
  if (!tblW) return null;

  const w = getAttr(tblW, 'w');
  const type = getAttr(tblW, 'type');
  if (!w) return null;

  const numVal = parseInt(w, 10);
  if (type === 'pct') {
    // Value is in fiftieths of a percent
    const pct = numVal / 50;
    return `${pct}%`;
  } else if (type === 'dxa') {
    return `${twipsToPx(numVal)}px`;
  } else if (type === 'auto' || type === 'nil') {
    return null;
  }
  // Default: treat as dxa
  if (numVal > 0) return `${twipsToPx(numVal)}px`;
  return null;
}

// ---------------------------------------------------------------------------
// Table parser (entry point)
// ---------------------------------------------------------------------------

/**
 * Parse a <w:tbl> element into a ProseMirror table node.
 */
export function parseTable(tblEl: Element, ctx: ParseContext): PMNode {
  // Parse column widths from <w:tblGrid>
  const colWidths = parseTableGrid(tblEl);

  // Parse table-level defaults (borders, cell margins)
  const tableDefaults = parseTableDefaults(tblEl);

  // Resolve table style (conditional formatting, banded rows, etc.)
  const tableStyle = resolveTableStyle(tblEl, ctx);

  // Merge style-level borders into table defaults (style borders are lower priority than explicit)
  if (tableStyle.styleBorders) {
    const sb = tableStyle.styleBorders;
    if (!tableDefaults.borders.top && sb.top) tableDefaults.borders.top = sb.top;
    if (!tableDefaults.borders.bottom && sb.bottom) tableDefaults.borders.bottom = sb.bottom;
    if (!tableDefaults.borders.left && sb.left) tableDefaults.borders.left = sb.left;
    if (!tableDefaults.borders.right && sb.right) tableDefaults.borders.right = sb.right;
    if (!tableDefaults.borders.insideH && sb.insideH) tableDefaults.borders.insideH = sb.insideH;
    if (!tableDefaults.borders.insideV && sb.insideV) tableDefaults.borders.insideV = sb.insideV;
  }

  // Parse table width
  const tableWidth = parseTableWidth(tblEl);

  // Parse rows
  const rows: PMNode[] = [];
  const children = tblEl.childNodes;

  // Track vertical merge state: map from column index → { startRow, rowspan }
  const vMergeStarts: Map<number, { rowIdx: number; node: PMNode }> = new Map();

  // Comment range markers can appear as direct children of <w:tbl> when a
  // comment spans entire rows or the table boundary. Forward them to the
  // CommentTracker so the comment is correctly activated/deactivated.
  const commentTracker = ctx.commentTracker as CommentTracker | undefined;

  // Count total rows for insideH/insideV border logic
  const rowEls: Element[] = [];
  // Also collect non-row elements in order so we can process comment markers
  // that appear between rows.
  interface TblChild { type: 'tr'; el: Element; }
  interface TblComment { type: 'commentRangeStart' | 'commentRangeEnd'; id: string; }
  type TblItem = TblChild | TblComment;
  const tblItems: TblItem[] = [];

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');
    if (localName === 'tr') {
      rowEls.push(el);
      tblItems.push({ type: 'tr', el });
    } else if (localName === 'commentRangeStart') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id) tblItems.push({ type: 'commentRangeStart', id });
    } else if (localName === 'commentRangeEnd') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id) tblItems.push({ type: 'commentRangeEnd', id });
    }
  }

  let rowCounter = 0;
  for (const item of tblItems) {
    if (item.type === 'tr') {
      const row = parseTableRow(
        (item as TblChild).el, rowCounter, rowEls.length, colWidths, vMergeStarts, tableDefaults, tableStyle, ctx
      );
      if (row) rows.push(row);
      rowCounter++;
    } else if (commentTracker) {
      const ci = item as TblComment;
      if (ci.type === 'commentRangeStart') {
        commentTracker.handleCommentRangeStart(ci.id);
      } else {
        commentTracker.handleCommentRangeEnd(ci.id);
      }
    }
  }

  const tableAttrs: Record<string, unknown> = {};
  if (tableWidth) tableAttrs.tableWidth = tableWidth;

  // Compute columnSize percentages from colWidths for tiptap-table-plus grid layout.
  // TablePlusNodeView reads the "columnSize" attr as a comma-separated string of
  // percentage values to set --cell-percentage CSS variable on the table wrapper.
  if (colWidths.length > 0) {
    const totalWidth = colWidths.reduce((a, b) => a + b, 0);
    if (totalWidth > 0) {
      // Enforce a minimum percentage per column so narrow columns don't clip content.
      // Word tables are often designed for wider pages; our editor is narrower.
      const MIN_COL_PCT = 10;
      let percentages = colWidths.map(w => (w / totalWidth) * 100);

      // Boost any columns below the minimum, then redistribute
      const belowMin = percentages.filter(p => p < MIN_COL_PCT);
      if (belowMin.length > 0 && belowMin.length < percentages.length) {
        const deficit = belowMin.reduce((sum, p) => sum + (MIN_COL_PCT - p), 0);
        const aboveMinTotal = percentages
          .filter(p => p >= MIN_COL_PCT)
          .reduce((sum, p) => sum + p, 0);
        percentages = percentages.map(p => {
          if (p < MIN_COL_PCT) return MIN_COL_PCT;
          // Shrink wider columns proportionally to compensate
          return p - (deficit * (p / aboveMinTotal));
        });
      }

      const rounded = percentages.map(p => Math.round(p));
      // Ensure percentages sum to exactly 100
      const sum = rounded.reduce((a, b) => a + b, 0);
      if (sum !== 100 && rounded.length > 0) {
        rounded[rounded.length - 1] += 100 - sum;
      }
      tableAttrs.columnSize = rounded.join(',');
    }
  }

  const tableNode: PMNode = {
    type: 'table',
    attrs: Object.keys(tableAttrs).length > 0 ? tableAttrs : undefined,
    content: rows,
  };

  return tableNode;
}

/**
 * Parse <w:tblGrid> to extract column widths in pixels.
 */
function parseTableGrid(tblEl: Element): number[] {
  const widths: number[] = [];
  const tblGrid = getChildNS(tblEl, W_NS, 'tblGrid');
  if (!tblGrid) return widths;

  const gridCols = tblGrid.childNodes;
  for (let i = 0; i < gridCols.length; i++) {
    const child = gridCols[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');
    if (localName === 'gridCol') {
      const w = el.getAttributeNS(W_NS, 'w') || el.getAttribute('w:w');
      if (w) {
        const pts = twipsToPoints(parseInt(w, 10));
        widths.push(Math.round(pts * 1.333));
      }
    }
  }

  return widths;
}

// ---------------------------------------------------------------------------
// Row parser
// ---------------------------------------------------------------------------

/**
 * Parse a <w:tr> element into a ProseMirror tableRow node.
 */
function parseTableRow(
  trEl: Element,
  rowIdx: number,
  totalRows: number,
  colWidths: number[],
  vMergeStarts: Map<number, { rowIdx: number; node: PMNode }>,
  tableDefaults: TableDefaults,
  tableStyle: ResolvedTableStyle,
  ctx: ParseContext
): PMNode | null {
  // Check if this is a header row
  const trPr = getChildNS(trEl, W_NS, 'trPr');
  const isHeader = trPr ? !!getChildNS(trPr, W_NS, 'tblHeader') : false;

  // Parse row height from <w:trHeight>
  let rowHeight: number | undefined;
  if (trPr) {
    const trHeightEl = getChildNS(trPr, W_NS, 'trHeight');
    if (trHeightEl) {
      const val = getAttr(trHeightEl, 'val');
      if (val) {
        rowHeight = twipsToPx(parseInt(val, 10));
      }
    }
  }

  // Comment range markers can appear as direct children of <w:tr> when a
  // comment spans cell boundaries. Forward them to the CommentTracker.
  const commentTracker = ctx.commentTracker as CommentTracker | undefined;

  // Count cells and collect comment markers in document order
  const cellEls: Element[] = [];
  interface TrComment { type: 'commentRangeStart' | 'commentRangeEnd'; id: string; }
  const trItems: Array<{ type: 'tc'; idx: number } | TrComment> = [];
  const children = trEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');
    if (localName === 'tc') {
      trItems.push({ type: 'tc', idx: cellEls.length });
      cellEls.push(el);
    } else if (localName === 'commentRangeStart') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id) trItems.push({ type: 'commentRangeStart', id });
    } else if (localName === 'commentRangeEnd') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id) trItems.push({ type: 'commentRangeEnd', id });
    }
  }

  const cells: PMNode[] = [];
  let colIdx = 0;

  for (const trItem of trItems) {
    if (trItem.type === 'commentRangeStart' || trItem.type === 'commentRangeEnd') {
      if (commentTracker) {
        const ci = trItem as TrComment;
        if (ci.type === 'commentRangeStart') {
          commentTracker.handleCommentRangeStart(ci.id);
        } else {
          commentTracker.handleCommentRangeEnd(ci.id);
        }
      }
      continue;
    }
    const ci = (trItem as { type: 'tc'; idx: number }).idx;
    const cellResult = parseTableCell(
      cellEls[ci],
      rowIdx,
      colIdx,
      ci,
      cellEls.length,
      totalRows,
      colWidths,
      isHeader,
      vMergeStarts,
      tableDefaults,
      tableStyle,
      ctx
    );

    if (cellResult) {
      if (cellResult.node) {
        cells.push(cellResult.node);
      }
      colIdx += cellResult.colSpan;
    } else {
      colIdx++;
    }
  }

  if (cells.length === 0) return null;

  const rowAttrs: Record<string, unknown> = {};
  if (rowHeight) rowAttrs.height = rowHeight;

  return {
    type: 'tableRow',
    attrs: Object.keys(rowAttrs).length > 0 ? rowAttrs : undefined,
    content: cells,
  };
}

// ---------------------------------------------------------------------------
// Cell parser
// ---------------------------------------------------------------------------

interface CellParseResult {
  node: PMNode | null;
  colSpan: number;
}

/**
 * Parse a <w:tc> element into a ProseMirror tableCell or tableHeader node.
 */
function parseTableCell(
  tcEl: Element,
  rowIdx: number,
  colIdx: number,
  cellIdxInRow: number,
  totalCellsInRow: number,
  totalRows: number,
  colWidths: number[],
  isHeaderRow: boolean,
  vMergeStarts: Map<number, { rowIdx: number; node: PMNode }>,
  tableDefaults: TableDefaults,
  tableStyle: ResolvedTableStyle,
  ctx: ParseContext
): CellParseResult {
  const tcPr = getChildNS(tcEl, W_NS, 'tcPr');

  // Horizontal merge (colspan)
  let colSpan = 1;
  if (tcPr) {
    const gridSpanEl = getChildNS(tcPr, W_NS, 'gridSpan');
    if (gridSpanEl) {
      const val = getAttr(gridSpanEl, 'val');
      if (val) colSpan = parseInt(val, 10);
    }
  }

  // Vertical merge (rowspan)
  let vMergeType: 'restart' | 'continue' | null = null;
  if (tcPr) {
    const vMergeEl = getChildNS(tcPr, W_NS, 'vMerge');
    if (vMergeEl) {
      const val = getAttr(vMergeEl, 'val');
      vMergeType = val === 'restart' ? 'restart' : 'continue';
    }
  }

  // Handle vertical merge continuation
  if (vMergeType === 'continue') {
    const mergeStart = vMergeStarts.get(colIdx);
    if (mergeStart) {
      // Extend the merge-origin cell's rowspan to cover this slot and emit NO
      // cell here. Previously this incremented the rowspan AND returned a
      // placeholder cell, so the covered grid slot was represented twice —
      // producing invalid geometry (an extra empty column, shifted data). The
      // caller still advances colIdx by colSpan for a null node.
      const currentRowspan =
        (mergeStart.node.attrs?.rowspan as number) || 1;
      mergeStart.node.attrs = {
        ...mergeStart.node.attrs,
        rowspan: currentRowspan + 1,
      };
      return { node: null, colSpan };
    }

    // Orphan continuation (no matching restart cell found) — emit a placeholder
    // so the row isn't short a column.
    const placeholderAttrs: Record<string, unknown> = {};
    if (colSpan > 1) placeholderAttrs.colspan = colSpan;
    if (colWidths.length > 0) {
      const widths: number[] = [];
      for (let c = colIdx; c < colIdx + colSpan && c < colWidths.length; c++) {
        widths.push(colWidths[c]);
      }
      if (widths.length > 0) placeholderAttrs.colwidth = widths;
    }

    const placeholderNode: PMNode = {
      type: isHeaderRow ? 'tableHeader' : 'tableCell',
      attrs: Object.keys(placeholderAttrs).length > 0 ? placeholderAttrs : undefined,
      content: [{ type: 'paragraph' }],
    };

    return { node: placeholderNode, colSpan };
  }

  // Parse cell content (paragraphs)
  const content: PMNode[] = [];
  const children = tcEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    if (localName === 'p') {
      content.push(parseParagraph(el, ctx));
    } else if (localName === 'tbl') {
      content.push(parseTable(el, ctx));
    } else if (localName === 'sdt') {
      const sdtContent = findSdtContent(el);
      if (sdtContent) {
        const sdtChildren = sdtContent.childNodes;
        for (let j = 0; j < sdtChildren.length; j++) {
          const sc = sdtChildren[j];
          if (sc.nodeType !== 1) continue;
          const scEl = sc as Element;
          const scName = scEl.localName || scEl.nodeName.replace(/^w:/, '');
          if (scName === 'p') content.push(parseParagraph(scEl, ctx));
          else if (scName === 'tbl') content.push(parseTable(scEl, ctx));
        }
      }
    } else if (localName === 'commentRangeStart' || localName === 'commentRangeEnd') {
      const commentTracker = ctx.commentTracker as import('./commentParser').CommentTracker | undefined;
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id && commentTracker) {
        if (localName === 'commentRangeStart') commentTracker.handleCommentRangeStart(id);
        else commentTracker.handleCommentRangeEnd(id);
      }
    }
  }

  // Ensure cell has at least one paragraph
  if (content.length === 0) {
    content.push({ type: 'paragraph' });
  }

  // Build cell attributes
  const cellType = isHeaderRow ? 'tableHeader' : 'tableCell';
  const attrs: Record<string, unknown> = {};

  if (colSpan > 1) attrs.colspan = colSpan;

  // Column widths
  if (colWidths.length > 0) {
    const widths: number[] = [];
    for (let c = colIdx; c < colIdx + colSpan && c < colWidths.length; c++) {
      widths.push(colWidths[c]);
    }
    if (widths.length > 0) attrs.colwidth = widths;
  }

  // Resolve table style conditional formatting for this cell
  const applicableConditionals = getApplicableConditionals(
    tableStyle, rowIdx, colIdx, totalRows, totalCellsInRow
  );

  // Background color from shading (explicit cell > conditional > whole-table style)
  let hasExplicitShading = false;
  if (tcPr) {
    const shdEl = getChildNS(tcPr, W_NS, 'shd');
    if (shdEl) {
      const fill = getAttr(shdEl, 'fill');
      if (fill && fill !== 'auto') {
        attrs.background = `#${fill}`;
        hasExplicitShading = true;
      }
    }
  }
  if (!hasExplicitShading) {
    // Apply conditional formatting shading (last matching conditional wins)
    let styleShading: string | undefined;
    for (const cond of applicableConditionals) {
      if (cond.cellShading) styleShading = cond.cellShading;
    }
    if (!styleShading && tableStyle.wholeTableShading) {
      styleShading = tableStyle.wholeTableShading;
    }
    if (styleShading) {
      attrs.background = `#${styleShading}`;
    }
  }

  // ---- NEW: Borders ----
  // Cell-level borders override table-level defaults.
  // Table-level insideH applies as top/bottom for interior cells;
  // insideV applies as left/right for interior cells.
  if (tcPr) {
    const tcBorders = parseBordersElement(getChildNS(tcPr, W_NS, 'tcBorders'));
    // Cell has its own borders — use them
    if (tcBorders.top) attrs.borderTop = tcBorders.top;
    if (tcBorders.bottom) attrs.borderBottom = tcBorders.bottom;
    if (tcBorders.left) attrs.borderLeft = tcBorders.left;
    if (tcBorders.right) attrs.borderRight = tcBorders.right;
  }

  // Fall back to table-level borders if cell didn't define its own
  const tb = tableDefaults.borders;
  if (!attrs.borderTop) {
    if (rowIdx === 0 && tb.top) {
      attrs.borderTop = tb.top;
    } else if (rowIdx > 0 && tb.insideH) {
      attrs.borderTop = tb.insideH;
    }
  }
  if (!attrs.borderBottom) {
    if (rowIdx === totalRows - 1 && tb.bottom) {
      attrs.borderBottom = tb.bottom;
    } else if (rowIdx < totalRows - 1 && tb.insideH) {
      attrs.borderBottom = tb.insideH;
    }
  }
  if (!attrs.borderLeft) {
    if (cellIdxInRow === 0 && tb.left) {
      attrs.borderLeft = tb.left;
    } else if (cellIdxInRow > 0 && tb.insideV) {
      attrs.borderLeft = tb.insideV;
    }
  }
  if (!attrs.borderRight) {
    if (cellIdxInRow === totalCellsInRow - 1 && tb.right) {
      attrs.borderRight = tb.right;
    } else if (cellIdxInRow < totalCellsInRow - 1 && tb.insideV) {
      attrs.borderRight = tb.insideV;
    }
  }

  // ---- NEW: Vertical alignment ----
  if (tcPr) {
    const vAlignEl = getChildNS(tcPr, W_NS, 'vAlign');
    if (vAlignEl) {
      const val = getAttr(vAlignEl, 'val');
      const vAlignMap: Record<string, string> = {
        top: 'top',
        center: 'middle',
        bottom: 'bottom',
        both: 'middle', // "both" is sometimes used for center
      };
      if (val && vAlignMap[val]) {
        attrs.verticalAlign = vAlignMap[val];
      }
    }
  }

  // ---- NEW: Cell padding/margins ----
  if (tcPr) {
    const tcMar = getChildNS(tcPr, W_NS, 'tcMar');
    if (tcMar) {
      const parseCellMargin = (name: string): number | undefined => {
        const el = getChildNS(tcMar!, W_NS, name);
        if (!el) return undefined;
        const w = getAttr(el, 'w');
        if (!w) return undefined;
        const type = getAttr(el, 'type');
        if (type && type !== 'dxa') return undefined;
        return Math.min(twipsToPx(parseInt(w, 10)), 5);
      };
      const pt = parseCellMargin('top');
      const pb = parseCellMargin('bottom');
      const pl = parseCellMargin('left') ?? parseCellMargin('start');
      const pr = parseCellMargin('right') ?? parseCellMargin('end');
      if (pt != null) attrs.paddingTop = pt;
      if (pb != null) attrs.paddingBottom = pb;
      if (pl != null) attrs.paddingLeft = pl;
      if (pr != null) attrs.paddingRight = pr;
    } else {
      // Fall back to table-level cell margins
      const tm = tableDefaults.cellMargins;
      if (tm.top != null) attrs.paddingTop = tm.top;
      if (tm.bottom != null) attrs.paddingBottom = tm.bottom;
      if (tm.left != null) attrs.paddingLeft = tm.left;
      if (tm.right != null) attrs.paddingRight = tm.right;
    }
  } else {
    // No tcPr at all — use table-level cell margins
    const tm = tableDefaults.cellMargins;
    if (tm.top != null) attrs.paddingTop = tm.top;
    if (tm.bottom != null) attrs.paddingBottom = tm.bottom;
    if (tm.left != null) attrs.paddingLeft = tm.left;
    if (tm.right != null) attrs.paddingRight = tm.right;
  }

  // Apply table style run properties to text nodes (font, color, bold, etc.)
  // These are defaults from the table style — only applied if the text doesn't already have them
  let styleRunProps: RunProperties | undefined;
  for (const cond of applicableConditionals) {
    if (cond.runProperties) {
      styleRunProps = styleRunProps
        ? { ...styleRunProps, ...cond.runProperties }
        : { ...cond.runProperties };
    }
  }
  if (!styleRunProps && tableStyle.wholeTableRunProps) {
    styleRunProps = tableStyle.wholeTableRunProps;
  }
  if (styleRunProps) {
    applyTableStyleRunProps(content, styleRunProps);
  }

  const cellNode: PMNode = {
    type: cellType,
    attrs: Object.keys(attrs).length > 0 ? attrs : undefined,
    content,
  };

  // Track this cell as a vMerge start point
  if (vMergeType === 'restart') {
    if (!cellNode.attrs) cellNode.attrs = {};
    cellNode.attrs.rowspan = 1;
    vMergeStarts.set(colIdx, { rowIdx, node: cellNode });
  } else {
    vMergeStarts.delete(colIdx);
  }

  return { node: cellNode, colSpan };
}

// ---------------------------------------------------------------------------
// Table style run property application
// ---------------------------------------------------------------------------

/**
 * Apply table-style run properties to text nodes inside cell content.
 * Only adds marks that aren't already present on the text node.
 * This handles font family, size, color, bold, etc. from table styles.
 */
function applyTableStyleRunProps(nodes: PMNode[], runProps: RunProperties): void {
  for (const node of nodes) {
    if (node.type === 'text' && node.text) {
      // Check what marks already exist
      const existingMarks = node.marks || [];
      const hasTextStyle = existingMarks.some(m => m.type === 'textStyle');
      const hasBold = existingMarks.some(m => m.type === 'bold');
      const hasItalic = existingMarks.some(m => m.type === 'italic');

      // Add bold/italic marks if not already present
      if (runProps.bold && !hasBold) {
        if (!node.marks) node.marks = [];
        node.marks.push({ type: 'bold' });
      }
      if (runProps.italic && !hasItalic) {
        if (!node.marks) node.marks = [];
        node.marks.push({ type: 'italic' });
      }

      // Add textStyle mark for color, fontSize, fontFamily
      const needsColor = runProps.color && !hasTextStyle;
      const needsFontSize = runProps.fontSize && !hasTextStyle;
      const needsFontFamily = runProps.fontFamily && !hasTextStyle;

      if (needsColor || needsFontSize || needsFontFamily) {
        if (!node.marks) node.marks = [];
        const textStyleAttrs: Record<string, string> = {};
        if (runProps.color) textStyleAttrs.color = `#${runProps.color}`;
        if (runProps.fontSize) textStyleAttrs.fontSize = `${runProps.fontSize}pt`;
        if (runProps.fontFamily) textStyleAttrs.fontFamily = runProps.fontFamily;

        // If there's already a textStyle mark, merge into it
        const existingTS = node.marks.find(m => m.type === 'textStyle');
        if (existingTS) {
          if (!existingTS.attrs) existingTS.attrs = {};
          if (!existingTS.attrs.color && textStyleAttrs.color) existingTS.attrs.color = textStyleAttrs.color;
          if (!existingTS.attrs.fontSize && textStyleAttrs.fontSize) existingTS.attrs.fontSize = textStyleAttrs.fontSize;
          if (!existingTS.attrs.fontFamily && textStyleAttrs.fontFamily) existingTS.attrs.fontFamily = textStyleAttrs.fontFamily;
        } else {
          node.marks.push({ type: 'textStyle', attrs: textStyleAttrs });
        }
      }
    }

    // Recurse into child nodes (paragraphs, etc.)
    if (node.content) {
      applyTableStyleRunProps(node.content, runProps);
    }
  }
}
